import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Crown, Award, Users } from 'lucide-react';
import { gamificationService } from '../../services';
import PageContainer from '../../components/PageContainer';
import Card from '../../components/ui/Card';
import { LoadingSpinner } from '../../components/ui/Loading';
import { getClassTypeIcon, getClassTypeCollor, CLASS_TYPES, CLASSES, getClassTypeTranslation } from '../../utils/characterTranslation';

const AdminRanking = () => {
  const [rankingByClass, setRankingByClass] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRankings();
  }, []);

  const loadRankings = async () => {
    try {
      setLoading(true);
      setError('');

      // Carregar ranking para cada classe
      const promises = CLASSES.sort().map(async (className) => {
        try {
          const response = await gamificationService.getRankingLevel({
            class: className,
            limit: 3
          });
          return { className, ranking: response.ranking || [] };
        } catch (error) {
          console.error(`Erro ao carregar ranking da classe ${className}:`, error);
          return { className, ranking: [] };
        }
      });

      const results = await Promise.all(promises);
      
      // Organizar por classe
      const rankingData = {};
      results.forEach((result) => {
        if (result && result.className) {
          rankingData[result.className] = result.ranking || [];
        }
      });

      setRankingByClass(rankingData);
    } catch (err) {
      console.error('Erro ao carregar rankings:', err);
      setError(err.response?.data?.error || 'Erro ao carregar rankings por classe');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (position) => {
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return null;
  };

  if (loading) {
    return (
      <PageContainer title="Rankings por Classe" description="Carregando...">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner text="Carregando rankings..." />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      icon={Trophy}
      title="Rankings por Classe"
      description="Top 3 personagens de cada classe"
    >
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/20 text-red-400 px-6 py-4 rounded-xl mb-6"
        >
          <p className="font-medium">{error}</p>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CLASSES.map((className, index) => {
          const classIcon = getClassTypeIcon(className);
          const colorClass = getClassTypeCollor(className);
          const ranking = rankingByClass[className] || [];

          return (
            <motion.div
              key={className}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card variant="glass" className="h-full group hover:scale-[1.02] transition-all duration-300 relative overflow-hidden">
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-blue-600/0 group-hover:from-cyan-500/10 group-hover:to-blue-600/10 transition-all duration-300 pointer-events-none"></div>
                
                <div className="relative p-6">
                  {/* Cabeçalho da Classe */}
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-2xl">
                      {classIcon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {getClassTypeTranslation(className)}
                      </h3>
                      <p className="text-sm text-gray-400">
                        Top 3 Jogadores
                      </p>
                    </div>
                  </div>

                  {/* Lista de Top 3 */}
                  <div className="space-y-3">
                    {ranking.length > 0 ? (
                      ranking.map((character, position) => (
                        <motion.div
                          key={character.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 + position * 0.1 }}
                          className={`
                            flex items-center gap-3 p-3 rounded-lg
                            ${position === 0 ? 'bg-yellow-500/10 border border-yellow-500/20' : ''}
                            ${position === 1 ? 'bg-gray-500/10 border border-gray-500/20' : ''}
                            ${position === 2 ? 'bg-amber-600/10 border border-amber-600/20' : ''}
                            ${position > 2 ? 'bg-white/5' : ''}
                            transition-all duration-300 hover:bg-white/10
                          `}
                        >
                          {/* Posição e Ícone */}
                          <div className="flex-shrink-0 w-10 text-center">
                            {getRankIcon(position + 1)}
                          </div>

                          {/* Info do Personagem */}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-semibold truncate">
                              {character.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-400">
                                Level {character.level}
                              </span>
                            </div>
                          </div>

                          {/* Pontuação */}
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1">
                              <Trophy className="w-4 h-4 text-cyan-400" />
                              <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">
                                {character.totalPoints?.toLocaleString() || 0}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400">pontos</p>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400 text-sm">
                          Nenhum personagem desta classe ainda
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Estatísticas Gerais */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-8"
      >
        <Card variant="glass">
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-cyan-400" />
              Estatísticas Gerais
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {CLASSES.map((className) => {
                const ranking = rankingByClass[className] || [];
                const totalPoints = ranking.reduce((sum, char) => sum + (char.totalPoints || 0), 0);
                const colorClass = getClassTypeCollor(className);
                
                return (
                  <div 
                    key={className}
                    className="p-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300"
                  >
                    <p className="text-xs text-gray-400 mb-1">
                      {getClassTypeTranslation(className)}
                    </p>
                    <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">
                      {ranking.length}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {totalPoints.toLocaleString()} pts
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </motion.div>
    </PageContainer>
  );
};

export default AdminRanking;
